import {FormEvent,useEffect,useRef,useState} from 'react';
import {ApiError,confirmInboundIntakeReview,InboundIntakeAttemptStatus,InboundIntakeNewFileInput,InboundIntakeQueueItem,InboundIntakeSourceDetail,loadInboundIntakeAttemptStatus,loadInboundIntakeDetail,loadInboundIntakeQueue,scanInboundIntakeAttachment} from './api';
import {DocumentTitleList,documentTitles} from './DocumentTitleList';

type Decision='create_file'|'link_existing_file'|'reject';

/**
 * The complete, immutable request a confirm() attempt submitted for one source -- not just its
 * idempotency key. Persisted to localStorage (survives a full page reload/remount, not just a
 * close/reopen within one mount) so that if the response to this exact request is ever lost,
 * nothing about what was actually submitted has to be reconstructed from memory: the same
 * key+payload can be safely resubmitted, or, once a readback confirms the source already left
 * `needs_review`, discarded as truthfully resolved.
 */
type StoredAttempt={
  idempotencyKey:string;expectedSourceVersion:number;decisionCode:Decision;approvedAttachmentIds:string[];
  newFileInput?:InboundIntakeNewFileInput;existingCompanyFileId?:string;contactName?:string;contactEmail?:string;
};

// Scoped to the organization and the signed-in identity, never global: an unscoped key would let
// one company's sender, subject, contact and job details sit readable in a shared browser
// profile's storage for whichever tenant's reviewer opens Mission Control next (Independent review
// 954/6308642451, Finding 3), and an organization-only key would let a second administrator of the
// same company, in the same browser, read, resubmit or discard the first administrator's
// unconfirmed attempt under their own session (independent exact-head review of ae26ea61).
type StorageScope={organizationId:string;identityId:string};
function attemptsStorageKey(scope:StorageScope):string{return `inbound-intake-review:pending-attempts:${scope.organizationId}:${scope.identityId}`}

function isValidStoredAttempt(value:unknown):value is StoredAttempt{
  if(typeof value!=='object'||value===null)return false;
  const v=value as Record<string,unknown>;
  return typeof v.idempotencyKey==='string' && v.idempotencyKey.length>0
    && typeof v.expectedSourceVersion==='number'
    && (v.decisionCode==='create_file'||v.decisionCode==='link_existing_file'||v.decisionCode==='reject')
    && Array.isArray(v.approvedAttachmentIds) && v.approvedAttachmentIds.every(id=>typeof id==='string');
}

/**
 * `unavailable: true` means the stored record itself could not be read (corrupt JSON, blocked
 * storage) -- distinct from a genuinely empty record. Treating unreadable storage as "no prior
 * attempt" would silently discard the one signal that an operation's outcome is unconfirmed
 * (independent review 954/6308642451, Finding 3); the caller surfaces this as a standing warning
 * instead.
 */
function loadStoredAttempts(scope:StorageScope):{attempts:Map<string,StoredAttempt>;unavailable:boolean}{
  try{
    const raw=localStorage.getItem(attemptsStorageKey(scope));
    if(raw===null)return{attempts:new Map(),unavailable:false};
    const parsed=JSON.parse(raw) as Record<string,unknown>;
    const attempts=new Map<string,StoredAttempt>();
    for(const[sourceId,value] of Object.entries(parsed))if(isValidStoredAttempt(value))attempts.set(sourceId,value);
    return{attempts,unavailable:false};
  }catch{
    return{attempts:new Map(),unavailable:true};
  }
}

/**
 * Returns whether the write actually durably landed. Independent review 954/6314769256, Finding
 * 1: a caller that ignores this and sends the network command anyway risks exactly the failure
 * this whole recovery mechanism exists to prevent -- a lost response with no record of what was
 * submitted to recover it from. Every call site that is about to submit a *new* confirm() must
 * check the return value and refuse to send when it is false; a call site only removing an
 * already-resolved entry may treat it as best-effort, since nothing new is at risk there.
 */
function saveStoredAttempts(scope:StorageScope,attempts:Map<string,StoredAttempt>):boolean{
  try{localStorage.setItem(attemptsStorageKey(scope),JSON.stringify(Object.fromEntries(attempts)));return true}
  catch{return false}
}

/** Compares everything about an attempt except its key: whether a resubmission is the exact same request. */
function attemptSignature(a:Omit<StoredAttempt,'idempotencyKey'>):string{
  return JSON.stringify({v:a.expectedSourceVersion,d:a.decisionCode,ids:[...a.approvedAttachmentIds].sort(),f:a.newFileInput??null,e:a.existingCompanyFileId??null,n:a.contactName??null,m:a.contactEmail??null});
}

// Server responses that reject the request itself (validation, stale version, missing source),
// as opposed to auth, rate-limit or availability failures. Only these trigger a re-read of the
// attempt status; none of them is treated as proof the original cannot still commit.
const DEFINITIVE_REJECTIONS=new Set([400,404,409,422]);

function emptyNewFile(){return {caption:'',caseNumber:'',court:'',stateCode:'',county:'',subject:'',serviceAddress:'',deadlineDate:''};}

// A deadline ends only this read observer; it never retries a request or a command.
function observeQueueRead<T>(request:Promise<T>,ready:(value:T)=>void,failed:()=>void){
  let active=true;
  const timer=setTimeout(()=>{if(active){active=false;failed()}},15000);
  request.then(value=>{if(active){active=false;clearTimeout(timer);ready(value)}})
    .catch(()=>{if(active){active=false;clearTimeout(timer);failed()}});
  return ()=>{active=false;clearTimeout(timer)};
}


/** A deadline ends this observer, never the server's scan or its durable ownership. */
async function observeAttachmentCheck<T>(request:Promise<T>,milliseconds:number):Promise<T>{
  let timer:ReturnType<typeof setTimeout>|undefined;
  try{
    return await Promise.race([request,new Promise<never>((_,reject)=>{timer=setTimeout(()=>reject(new Error('Attachment status is unknown.')),milliseconds)})]);
  }finally{if(timer!==undefined)clearTimeout(timer)}
}

export function InboundIntakeReview({organizationId,identityId}:{organizationId:string;identityId:string}){
  const storageScope:StorageScope={organizationId,identityId};
  const[items,setItems]=useState<InboundIntakeQueueItem[]|null>(null);
  const[failed,setFailed]=useState(false);
  const[queueRefresh,setQueueRefresh]=useState(0);
  const[openSourceId,setOpenSourceId]=useState('');
  const[detail,setDetail]=useState<InboundIntakeSourceDetail|null>(null);
  const[detailFailed,setDetailFailed]=useState(false);
  // Set instead of `detail` when a stored attempt's own confirm() already committed for this
  // source (review() ran) but its outcome was never confirmed to the caller -- detail() would
  // only 404 here, since the source has already left `needs_review`. Mutually exclusive with
  // `detail`: resubmitting the identical stored request is the only recovery action offered.
  const[resuming,setResuming]=useState<StoredAttempt|null>(null);
  const[decision,setDecision]=useState<Decision>('reject');
  const[approved,setApproved]=useState<Set<string>>(new Set());
  const[contactName,setContactName]=useState('');
  const[contactEmail,setContactEmail]=useState('');
  const[existingCompanyFileId,setExistingCompanyFileId]=useState('');
  const[newFile,setNewFile]=useState(emptyNewFile());
  const[documentsToServe,setDocumentsToServe]=useState('');
  const[pending,setPending]=useState(false);
  const[status,setStatus]=useState('');
  const statusRef=useRef<HTMLParagraphElement>(null);
  // Every source with an attempt whose outcome is still unknown, keyed by source_id. Lazily
  // hydrated from localStorage on first use (not on every render) and kept in sync with it on
  // every mutation, so the record of exactly what was submitted survives a full page reload.
  const attemptsRefHolder=useRef<Map<string,StoredAttempt>|undefined>(undefined);
  const unavailableAtLoadRef=useRef<boolean|undefined>(undefined);
  if(attemptsRefHolder.current===undefined){
    const loaded=loadStoredAttempts(storageScope);
    attemptsRefHolder.current=loaded.attempts;
    unavailableAtLoadRef.current=loaded.unavailable;
  }
  const attemptsRef=attemptsRefHolder as {current:Map<string,StoredAttempt>};
  const[storageUnavailable]=useState(()=>unavailableAtLoadRef.current===true);
  // Mirrors attemptsRef's keys into state purely so the "unconfirmed decisions" list below
  // re-renders when the ref map changes -- the ref itself is intentionally not state, since every
  // other reader of it (resolveStoredAttempt, submit, resumeDecision) wants the latest value
  // synchronously, not a render-batched one. Independent review 954/6314769256, Finding 4: a
  // source whose confirm() already committed but left needs_review has no visible trace in the
  // ordinary queue once this component reloads it -- only this list can surface it for recovery.
  const[pendingSourceIds,setPendingSourceIds]=useState<string[]>(()=>Array.from(attemptsRef.current.keys()));
  // Every read and write below goes back to storage itself rather than trusting this tab's
  // in-memory snapshot: two tabs of the same reviewer share one record, and writing back a whole
  // stale snapshot would silently erase the other tab's unconfirmed attempt (exact-head review of
  // the #956-rebased #954). Each write changes only its own source's entry, compare-and-set on the
  // entry it last read; each removal only removes the exact attempt (by key) it resolved.
  function syncFromStorage(attempts:Map<string,StoredAttempt>){
    attemptsRef.current=attempts;
    setPendingSourceIds(Array.from(attempts.keys()));
  }
  function readAttempt(sourceId:string):StoredAttempt|undefined{
    const loaded=loadStoredAttempts(storageScope);
    if(!loaded.unavailable)syncFromStorage(loaded.attempts);
    return attemptsRef.current.get(sourceId);
  }
  // Every read-check-write of the shared record runs under one Web Lock scoped to this
  // organization+identity, so no two tabs can interleave a compare-and-set on it: separate tabs'
  // getItem/setItem pairs are not otherwise a cross-context transaction. Where Web Locks are
  // unavailable, changing the record is refused outright rather than attempted unlocked
  // (exact-head review of 93efb431, Finding 2).
  function withRecordLock<T>(fn:()=>T):Promise<{locked:true;value:T}|{locked:false}>{
    const locks=(globalThis.navigator as Navigator|undefined)?.locks;
    if(!locks||typeof locks.request!=='function')return Promise.resolve({locked:false});
    return locks.request(`${attemptsStorageKey(storageScope)}:lock`,()=>({locked:true as const,value:fn()})) as Promise<{locked:true;value:T}>;
  }
  async function writeAttempt(sourceId:string,attempt:StoredAttempt,expectedKey:string|undefined):Promise<'saved'|'changed_elsewhere'|'unsaved'|'no_lock'>{
    const result=await withRecordLock(()=>writeAttemptNow(sourceId,attempt,expectedKey));
    return result.locked?result.value:'no_lock';
  }
  function writeAttemptNow(sourceId:string,attempt:StoredAttempt,expectedKey:string|undefined):'saved'|'changed_elsewhere'|'unsaved'{
    const loaded=loadStoredAttempts(storageScope);
    // The record cannot be verified right now (corrupt JSON, blocked storage): treating that the
    // same as "confirmed empty" would let a brand-new write silently replace an unreadable-but-
    // real prior attempt for this or another source. Refuse rather than guess (exact-head review
    // of 757b0917, Finding 1).
    if(loaded.unavailable)return 'unsaved';
    const current=loaded.attempts.get(sourceId);
    if(current?.idempotencyKey!==expectedKey){syncFromStorage(loaded.attempts);return 'changed_elsewhere'}
    // An unchanged retry of an attempt that is already durably recorded needs no new write; a
    // failed rewrite of it must never be mistaken for "nothing recorded" (exact-head review of
    // 93efb431, Finding 1). This instance's memory is only ever replaced by a successful write.
    if(current!==undefined&&current.idempotencyKey===attempt.idempotencyKey&&attemptSignature(current)===attemptSignature(attempt)){syncFromStorage(loaded.attempts);return 'saved'}
    const next=new Map(loaded.attempts);next.set(sourceId,attempt);
    if(!saveStoredAttempts(storageScope,next))return 'unsaved';
    syncFromStorage(next);
    return 'saved';
  }
  // Best-effort: without the lock the resolved record is simply left in place, which is harmless --
  // its next status check reports it already decided and clears it then.
  async function clearAttempt(sourceId:string,idempotencyKey:string):Promise<void>{
    await withRecordLock(()=>clearAttemptNow(sourceId,idempotencyKey));
  }
  /** True only when the exact attempt (key and payload) is durably readable from storage right now. */
  function isDurablyRecorded(sourceId:string,attempt:StoredAttempt):boolean{
    const loaded=loadStoredAttempts(storageScope);
    if(loaded.unavailable)return false;
    const stored=loaded.attempts.get(sourceId);
    return stored!==undefined&&stored.idempotencyKey===attempt.idempotencyKey&&attemptSignature(stored)===attemptSignature(attempt);
  }
  function clearAttemptNow(sourceId:string,idempotencyKey:string){
    const loaded=loadStoredAttempts(storageScope);
    if(loaded.unavailable){
      // The record cannot be verified right now, so it must not be overwritten with a
      // reconstruction from this tab's stale in-memory view -- that could discard another
      // source's (or another tab's) genuinely still-pending attempt that merely isn't readable at
      // this instant. Leave durable storage untouched; only this tab's own view of the one
      // operation the caller already confirmed resolved is updated (exact-head review of
      // 757b0917, Finding 1).
      const next=new Map(attemptsRef.current);
      next.delete(sourceId);
      syncFromStorage(next);
      return;
    }
    const attempts=loaded.attempts;
    if(attempts.get(sourceId)?.idempotencyKey===idempotencyKey){
      attempts.delete(sourceId);
      saveStoredAttempts(storageScope,attempts); // best-effort: nothing new is at risk when removing a resolved attempt
    }
    syncFromStorage(attempts);
  }
  useEffect(()=>{
    // Another tab's write: refresh the shared record so this tab never acts on, or re-saves, a stale copy.
    const key=attemptsStorageKey({organizationId,identityId});
    const onStorage=(event:StorageEvent)=>{
      if(event.key!==key)return;
      const loaded=loadStoredAttempts({organizationId,identityId});
      if(!loaded.unavailable){attemptsRef.current=loaded.attempts;setPendingSourceIds(Array.from(loaded.attempts.keys()))}
    };
    window.addEventListener('storage',onStorage);
    return ()=>window.removeEventListener('storage',onStorage);
  },[organizationId,identityId]);
  // A monotonic id distinguishing every individual openForReview() call, including two calls for
  // the very same source_id (reopen after close). A detail() response is applied only if it is
  // still the most recently issued request when it resolves -- guards both cross-source
  // clobbering (open A, then B, before A's slow response lands) and same-source clobbering (open
  // A, open B, then reopen A -- the first A request's very late response must not override the
  // second, later A request).
  const latestRequestRef=useRef(0);
  const scanInFlightRef=useRef(false);
  const scopeRef=useRef('');
  scopeRef.current=`${organizationId}:${identityId}`;
  const[scanningObjectId,setScanningObjectId]=useState('');
  const[scanUnknown,setScanUnknown]=useState(false);
  useEffect(()=>()=>{latestRequestRef.current+=1;scanInFlightRef.current=false},[]);


  useEffect(()=>{setFailed(false);setItems(null);return observeQueueRead(loadInboundIntakeQueue(),setItems,()=>setFailed(true))},[queueRefresh]);

  function restoreAttemptIntoForm(stored:StoredAttempt){
    setDecision(stored.decisionCode);
    setApproved(new Set(stored.approvedAttachmentIds));
    setContactName(stored.contactName??'');setContactEmail(stored.contactEmail??'');
    setExistingCompanyFileId(stored.existingCompanyFileId??'');
    if(stored.newFileInput){
      const job=stored.newFileInput.jobs[0];
      setNewFile({caption:stored.newFileInput.caption??'',caseNumber:stored.newFileInput.caseNumber,court:stored.newFileInput.court,stateCode:stored.newFileInput.stateCode,county:stored.newFileInput.county??'',subject:job.subject,serviceAddress:job.serviceAddress,deadlineDate:job.deadlineDate});
      setDocumentsToServe((job.documentsToServe??[]).join('\n'));
    }
  }

  /**
   * The authoritative read for an earlier, unconfirmed attempt: unlike `detail()`'s 404 (which
   * only proves `review()` itself committed, never that the attachment transfer/adoption/
   * completion loop inside `confirm()` actually finished), `attemptStatus()` reports the real
   * outcome of *this exact* stored attempt. 'unknown' means the check itself failed (denied,
   * offline, or any other error) -- never treated as either a finished or an in-progress outcome.
   */
  async function resolveStoredAttempt(sourceId:string,stored:StoredAttempt):Promise<'safe_to_submit'|'already_done'|'in_progress'|'unknown'>{
    let status:InboundIntakeAttemptStatus;
    try{status=await loadInboundIntakeAttemptStatus(sourceId,stored.idempotencyKey)}
    catch{return 'unknown'}
    if(!status.exists)return 'safe_to_submit';
    if(!status.is_own_attempt)return 'already_done'; // a different decision already landed for this source
    return status.decision_code==='reject'||status.conversion_lifecycle_state==='converted'?'already_done':'in_progress';
  }

  async function openForReview(sourceId:string){
    const requestId=++latestRequestRef.current;
    setOpenSourceId(sourceId);setDetail(null);setDetailFailed(false);setResuming(null);setScanUnknown(false);
    setDecision('reject');setApproved(new Set());setContactName('');setContactEmail('');
    setExistingCompanyFileId('');setNewFile(emptyNewFile());setDocumentsToServe('');setStatus('');

    const stored=readAttempt(sourceId);
    if(stored===undefined){
      loadInboundIntakeDetail(sourceId)
        .then(result=>{if(latestRequestRef.current===requestId)setDetail(result)})
        .catch(()=>{if(latestRequestRef.current===requestId)setDetailFailed(true)});
      return;
    }

    // A previous confirm() attempt for this source never confirmed its own outcome (lost
    // response, or the browser reloaded before it arrived). Find out the truth before showing a
    // form the reviewer might blindly resubmit or edit over an unknown result.
    const outcome=await resolveStoredAttempt(sourceId,stored);
    if(latestRequestRef.current!==requestId)return;

    if(outcome==='unknown'){
      setOpenSourceId('');
      setStatus(`Could not confirm the status of source ${sourceId}. Check your connection and try again.`);
      requestAnimationFrame(()=>statusRef.current?.focus());
      return;
    }
    if(outcome==='already_done'){
      await clearAttempt(sourceId,stored.idempotencyKey);
      setOpenSourceId('');
      setStatus(`Source ${sourceId} already has a recorded decision. Reloading the queue.`);
      setQueueRefresh(value=>value+1);
      requestAnimationFrame(()=>statusRef.current?.focus());
      return;
    }
    if(outcome==='in_progress'){
      // review() already committed for this exact stored attempt but the attachment loop inside
      // confirm() may not have finished -- the source has already left `needs_review`, so
      // detail() would only 404 here. Resubmitting the identical stored request is safe
      // (confirm()'s own per-step idempotency) and is the only recovery action offered.
      setResuming(stored);
      return;
    }
    // outcome === 'safe_to_submit': nothing committed yet for this source.
    restoreAttemptIntoForm(stored);
    loadInboundIntakeDetail(sourceId)
      .then(result=>{if(latestRequestRef.current===requestId)setDetail(result)})
      .catch(()=>{if(latestRequestRef.current===requestId)setDetailFailed(true)});
  }

  async function resumeDecision(){
    if(!resuming||pending)return;
    const sourceId=openSourceId;
    setPending(true);setStatus('');
    try{
      // Resume may only ever resend an attempt whose exact key and payload are durably recorded:
      // if that record cannot be read back right now, a lost response here would leave nothing to
      // recover from (exact-head review of 38660c43, Finding 1).
      if(!isDurablyRecorded(sourceId,resuming)){
        setStatus(`This browser could not confirm its saved record for source ${sourceId}, so nothing was sent. Free up storage space (or use a different browser) and try again.`);
        return;
      }
      await confirmInboundIntakeReview(sourceId,resuming.expectedSourceVersion,resuming.decisionCode,resuming.idempotencyKey,{
        approvedAttachmentIds:resuming.approvedAttachmentIds,newFileInput:resuming.newFileInput,
        existingCompanyFileId:resuming.existingCompanyFileId,contactName:resuming.contactName,contactEmail:resuming.contactEmail,
      });
      await clearAttempt(sourceId,resuming.idempotencyKey);
      setStatus(resuming.decisionCode==='reject'?'Source rejected.':'Review confirmed and conversion completed.');
      closeReview();
      setQueueRefresh(value=>value+1);
    }catch(error){
      // A refusal of this resubmission, and any attempt-status read after it, are point-in-time
      // observations: neither proves the first copy of this request (whose response was lost) cannot
      // still commit later. The stored original and its key are therefore kept unless a decision is
      // authoritatively recorded for this source; no path here unlocks an edited submission under a
      // new key (exact-head review of dac40dad).
      if(error instanceof ApiError&&DEFINITIVE_REJECTIONS.has(error.status)
        &&await resolveStoredAttempt(sourceId,resuming)==='already_done'){
        await clearAttempt(sourceId,resuming.idempotencyKey);
        setStatus(`Source ${sourceId} already has a recorded decision. Reloading the queue.`);
        closeReview();setQueueRefresh(value=>value+1);
        return;
      }
      setStatus(`The earlier decision for source ${sourceId} could not be confirmed. It is still recorded here and can only be resubmitted unchanged; try again later, or contact engineering if it keeps failing.`);
    }finally{
      setPending(false);
      requestAnimationFrame(()=>statusRef.current?.focus());
    }
  }

  function closeReview(){
    latestRequestRef.current+=1;
    setOpenSourceId('');setDetail(null);setResuming(null);
  }


  async function checkAttachment(objectId:string){
    if(!detail||pending||scanUnknown||scanInFlightRef.current||detail.attachment_scanning_available!==true)return;
    const sourceId=detail.source_id;
    const generation=latestRequestRef.current;
    const scope=scopeRef.current;
    const current=()=>latestRequestRef.current===generation&&scopeRef.current===scope;
    scanInFlightRef.current=true;setPending(true);setScanningObjectId(objectId);setStatus('Checking the stored attachment…');
    try{
      await observeAttachmentCheck(scanInboundIntakeAttachment(sourceId,objectId),90000);
      if(!current())return;
      const refreshed=await observeAttachmentCheck(loadInboundIntakeDetail(sourceId),15000);
      if(!current())return;
      setDetail(refreshed);
      const state=refreshed.message?.attachments.find(a=>a.source_object_id===objectId)?.lifecycle_state;
      setScanUnknown(state!=='clean'&&state!=='infected');
      setStatus(state==='clean'?'Attachment check is complete. You may review and approve it.':state==='infected'?'The attachment was flagged and cannot be approved.':'The check is not confirmed complete. Refresh attachment status; do not resubmit the check.');
    }catch{
      if(current()){setScanUnknown(true);setStatus('The attachment check result is unknown. Refresh attachment status before taking another action.')}
    }finally{
      if(current()){scanInFlightRef.current=false;setPending(false);setScanningObjectId('')}
    }
  }

  async function refreshAttachmentStatus(){
    if(!detail||pending||scanInFlightRef.current)return;
    const sourceId=detail.source_id,generation=latestRequestRef.current,scope=scopeRef.current;
    setPending(true);
    try{
      const refreshed=await observeAttachmentCheck(loadInboundIntakeDetail(sourceId),15000);
      if(latestRequestRef.current!==generation||scopeRef.current!==scope)return;
      setDetail(refreshed);setScanUnknown(false);setStatus('Attachment status refreshed. Only a clean attachment can be approved.');
    }catch{
      if(latestRequestRef.current===generation&&scopeRef.current===scope)setStatus('Attachment status could not be confirmed. No check was resubmitted.');
    }finally{
      if(latestRequestRef.current===generation&&scopeRef.current===scope)setPending(false);
    }
  }

  function toggleAttachment(sourceObjectId:string){
    setApproved(current=>{const next=new Set(current);if(next.has(sourceObjectId))next.delete(sourceObjectId);else next.add(sourceObjectId);return next});
  }

  async function submit(event:FormEvent){
    event.preventDefault();
    if(!detail||pending)return;
    setPending(true);setStatus('');
    const sourceId=detail.source_id;
    try{
      const approvedAttachmentIds=decision==='reject'?[]:Array.from(approved);
      const newFileInput:InboundIntakeNewFileInput|undefined=decision==='create_file'?{
        ...(newFile.caption.trim()?{caption:newFile.caption.trim()}:{}),
        caseNumber:newFile.caseNumber.trim(),court:newFile.court.trim(),stateCode:newFile.stateCode.trim(),
        ...(newFile.county.trim()?{county:newFile.county.trim()}:{}),
        jobs:[{subject:newFile.subject.trim(),serviceAddress:newFile.serviceAddress.trim(),deadlineDate:newFile.deadlineDate,documentsToServe:documentTitles(documentsToServe)}],
      }:undefined;
      const nextAttempt={
        expectedSourceVersion:detail.state_version,decisionCode:decision,approvedAttachmentIds,newFileInput,
        existingCompanyFileId:decision==='link_existing_file'?existingCompanyFileId.trim():undefined,
        contactName:decision==='reject'?undefined:contactName.trim(),
        contactEmail:decision==='reject'?undefined:contactEmail.trim(),
      };
      const existing=readAttempt(sourceId);
      let key:string;
      if(existing===undefined){
        key=crypto.randomUUID();
      }else if(attemptSignature(existing)===attemptSignature(nextAttempt)){
        key=existing.idempotencyKey;
      }else{
        // The form changed since the last attempt for this source whose outcome is still
        // unknown. Resubmitting the edited payload under that same key would either silently
        // supersede an attempt that may have already succeeded, or be bounced by the backend's
        // own fingerprint check -- neither tells the reviewer the truth. Find out first.
        const outcome=await resolveStoredAttempt(sourceId,existing);
        if(outcome==='unknown'){
          setStatus(`Could not confirm the status of source ${sourceId}. Check your connection and try again.`);
          return;
        }
        if(outcome==='already_done'){
          await clearAttempt(sourceId,existing.idempotencyKey);
          setStatus(`Source ${sourceId} has left the review queue; its decision was recorded. Check Mission Control Operations for the resulting file, or contact engineering if nothing appears there.`);
          closeReview();setQueueRefresh(value=>value+1);
          return;
        }
        if(outcome==='in_progress'){
          // The earlier, unedited attempt already committed and may still be finishing -- it is
          // not safe to submit the edited payload over it under a new key. Offer the one correct
          // recovery action (resubmitting the original attempt unchanged) instead.
          setDetail(null);setResuming(existing);
          setStatus(`A previous decision for source ${sourceId} was already submitted and may still be completing. Resume it instead of submitting changes.`);
          return;
        }
        // outcome === 'safe_to_submit' is only a point-in-time read: the earlier request (whose
        // response was lost, not necessarily its request) may still be in flight and commit after
        // this check. Sending the edited payload under a new key now would race it -- whichever
        // lands first wins, and replacing the stored record would discard the only key that can
        // recover the original if it wins. Keep the original request and key, and require it to be
        // resubmitted unchanged until its outcome is established (exact-head review of ae26ea61).
        setDetail(null);setResuming(existing);
        setStatus(`An earlier decision for source ${sourceId} was submitted but its result is not confirmed. Resubmit it unchanged first; changes cannot be submitted while its outcome is unconfirmed.`);
        return;
      }
      const attempt:StoredAttempt={idempotencyKey:key,...nextAttempt};
      // Recorded before sending: a lost response or a reload mid-request still leaves a truthful
      // record of exactly what was submitted for this source to recover from. If this record
      // cannot actually be written durably, sending the command anyway would create exactly the
      // failure this whole mechanism exists to prevent -- a lost response with nothing to recover
      // it from. Refuse to send rather than risk that (independent review 954/6314769256, Finding 1).
      const saved=await writeAttempt(sourceId,attempt,existing?.idempotencyKey);
      if(saved==='changed_elsewhere'){
        setStatus(`A decision for source ${sourceId} was just recorded in another tab or window. Close this review and check its status before submitting.`);
        return;
      }
      if(saved==='no_lock'){
        setStatus(`This browser cannot safely coordinate review decisions across tabs, so nothing was sent for source ${sourceId}. Use a current version of Chrome, Edge, Firefox or Safari.`);
        return;
      }
      if(saved==='unsaved'){
        setStatus(`This browser could not save a recovery record for source ${sourceId}. Free up storage space (or use a different browser) and try again.`);
        return;
      }
      await confirmInboundIntakeReview(sourceId,attempt.expectedSourceVersion,decision,key,{
        approvedAttachmentIds,newFileInput,
        existingCompanyFileId:attempt.existingCompanyFileId,
        contactName:attempt.contactName,contactEmail:attempt.contactEmail,
      });
      await clearAttempt(sourceId,key);
      setStatus(decision==='reject'?'Source rejected.':'Review confirmed and conversion completed.');
      closeReview();
      setQueueRefresh(value=>value+1);
    }catch{
      setStatus('The review could not be confirmed. Reload and try again.');
    }finally{
      setPending(false);
      requestAnimationFrame(()=>statusRef.current?.focus());
    }
  }

  return <section className="panel" aria-labelledby="inbound-intake-review-title">
    <p className="eyebrow">EMAIL INTAKE</p>
    <h2 id="inbound-intake-review-title">Inbound email review queue</h2>
    <p>Sender, subject and attachment labels below are sender-declared and unverified. Attachment bytes are never previewed here. An available attachment check scans the stored copy before an explicit review decision.</p>
    {storageUnavailable&&<p className="notice error">This browser's recovery record for unconfirmed decisions could not be read. If a decision was submitted here before a reload and its result was never confirmed, that cannot be detected from stored data -- check Mission Control Operations for each source you were reviewing.</p>}
    {pendingSourceIds.length>0&&<section aria-labelledby="inbound-intake-pending-title">
      {/* A source whose confirm() already committed can leave needs_review entirely, so it has no
          trace in the ordinary queue below once this component reloads -- this is the only place
          that still shows it, by source_id alone (no cached metadata is kept, so no sender/subject
          is shown here without a fresh server round trip). Independent review 954/6314769256,
          Finding 4. */}
      <h3 id="inbound-intake-pending-title">Unconfirmed decisions awaiting recovery</h3>
      <ul>{pendingSourceIds.map(sourceId=><li key={sourceId}>
        Source {sourceId} <button type="button" disabled={pending||openSourceId===sourceId} onClick={()=>openForReview(sourceId)}>Check status</button>
      </li>)}</ul>
    </section>}
    <p ref={statusRef} tabIndex={-1} role="status" aria-live="polite" className="notice">{status}</p>
    <div aria-live="polite" aria-atomic="true">
      {failed&&<p className="notice error">The inbound intake queue could not be loaded. Access was denied or the service is unavailable.</p>}
      {!failed&&items===null&&<p className="notice">Checking the inbound intake queue…</p>}
      {!failed&&items?.length===0&&<p className="notice">No inbound email sources are waiting for review.</p>}
    </div>
    {failed&&<button type="button" onClick={()=>setQueueRefresh(value=>value+1)}>Reload queue</button>}
    {items&&items.length>0&&<ol className="intake-list">{items.map(item=>
      <li className="intake-card" key={item.source_id}>
        <p className="workflow-state"><strong>Email Intake</strong> · unverified sender</p>
        <h3>{item.message?.subject_declared?item.message.subject||'(no subject)':'Message not yet available'}</h3>
        {item.message&&<p>From {item.message.sender} (sender-declared, unverified) · received {item.provider_received_at}</p>}
        <ul>{item.message?.attachments.map(a=><li key={a.ordinal}>{a.filename} ({a.media_type}) — sender-declared{a.lifecycle_state?` · ${a.lifecycle_state.replaceAll('_',' ')}`:''}</li>)}</ul>
        <p className="reference">Source {item.source_id} · version {item.state_version} · {item.attachment_count} attachment{item.attachment_count===1?'':'s'}</p>
        {/* Disabled while any confirm() is pending, not just for the currently open source: a
            reviewer must not be able to navigate to a different source while a decision for the
            open one is still in flight -- its own completion/status would otherwise land against
            whatever is open by the time it resolves. */}
        <button type="button" disabled={pending||openSourceId===item.source_id} onClick={()=>openForReview(item.source_id)}>Open for review</button>
      </li>
    )}</ol>}

    {openSourceId&&<section aria-labelledby="inbound-intake-detail-title">
      <h3 id="inbound-intake-detail-title">Reviewing {openSourceId}</h3>
      {resuming&&<div className="notice">
        <p>A previous decision for this source was already submitted but its result was never confirmed. Resubmitting it is safe -- it will either complete the original decision or report that it already completed.</p>
        <div className="mcfw-actions">
          <button type="button" onClick={closeReview} disabled={pending}>Close without resuming</button>
          <button type="button" onClick={resumeDecision} disabled={pending}>{pending?'Confirming…':'Resume decision'}</button>
        </div>
      </div>}
      {!resuming&&detailFailed&&<p className="notice error">This source could not be opened. It may have already been decided. Reload the queue and try again.</p>}
      {!resuming&&!detailFailed&&detail===null&&<p className="notice">Opening source…</p>}
      {!resuming&&detail&&<form onSubmit={submit}>
        <p>Subject: <strong>{detail.message?.subject_declared?detail.message.subject||'(no subject)':'Message not yet available'}</strong></p>
        {detail.message&&<p>From {detail.message.sender} (sender-declared, unverified)</p>}
        {detail.message?.body_declared&&<p>Body (sender-declared, unverified): {detail.message.body}</p>}
        <fieldset disabled={pending}>
          <legend>Decision</legend>
          <label><input type="radio" name="decision" value="reject" checked={decision==='reject'} onChange={()=>setDecision('reject')}/> Reject this source</label>
          <label><input type="radio" name="decision" value="create_file" checked={decision==='create_file'} onChange={()=>setDecision('create_file')}/> Create a new company file</label>
          <label><input type="radio" name="decision" value="link_existing_file" checked={decision==='link_existing_file'} onChange={()=>setDecision('link_existing_file')}/> Link to an existing company file</label>
        </fieldset>

        {decision!=='reject'&&detail.message&&detail.message.attachments.length>0&&<fieldset disabled={pending}>
          <legend>Approved attachments</legend>
          {detail.message.attachments.map(a=><div key={a.ordinal}><label>
            <input type="checkbox"
              disabled={a.source_object_id===null||a.lifecycle_state!=='clean'}
              checked={a.source_object_id!==null&&approved.has(a.source_object_id)}
              onChange={()=>a.source_object_id&&toggleAttachment(a.source_object_id)}
            /> {a.filename} ({a.media_type}){a.lifecycle_state!=='clean'?` — not yet approvable (${(a.lifecycle_state??'unavailable').replaceAll('_',' ')})`:''}
          </label>
          {detail.attachment_scanning_available===true&&a.source_object_id!==null&&a.lifecycle_state==='stored'&&<button type="button" disabled={pending||scanUnknown} onClick={()=>checkAttachment(a.source_object_id!)}>Check attachment {a.ordinal}</button>}
          </div>)}
          {detail.attachment_scanning_available===true&&<button type="button" disabled={pending} onClick={refreshAttachmentStatus}>Refresh attachment status</button>}
        </fieldset>}

        {decision!=='reject'&&<fieldset disabled={pending}>
          <legend>Sender contact</legend>
          <label>Contact name <input required value={contactName} onChange={event=>setContactName(event.target.value)}/></label>
          <label>Contact email <input type="email" required value={contactEmail} onChange={event=>setContactEmail(event.target.value)}/></label>
        </fieldset>}

        {decision==='link_existing_file'&&<fieldset disabled={pending}>
          <legend>Existing company file</legend>
          <label>Company file id <input required value={existingCompanyFileId} onChange={event=>setExistingCompanyFileId(event.target.value)} placeholder="Existing open file with exactly one open service job"/></label>
        </fieldset>}

        {decision==='create_file'&&<fieldset disabled={pending}>
          <legend>New company file (single job)</legend>
          <label>Case caption <input value={newFile.caption} onChange={event=>setNewFile({...newFile,caption:event.target.value})} placeholder="Smith v. Example Corp."/></label>
          <label>Case number <input required maxLength={96} value={newFile.caseNumber} onChange={event=>setNewFile({...newFile,caseNumber:event.target.value})}/></label>
          <label>Court <input required maxLength={191} value={newFile.court} onChange={event=>setNewFile({...newFile,court:event.target.value})}/></label>
          <label>State <input required maxLength={64} value={newFile.stateCode} onChange={event=>setNewFile({...newFile,stateCode:event.target.value})} placeholder="State of the issuing court"/></label>
          <label>County or court district (optional) <input maxLength={191} value={newFile.county} onChange={event=>setNewFile({...newFile,county:event.target.value})}/></label>
          <label>Person or business to serve <input required maxLength={191} value={newFile.subject} onChange={event=>setNewFile({...newFile,subject:event.target.value})}/></label>
          <label>Service address <textarea required rows={2} maxLength={191} value={newFile.serviceAddress} onChange={event=>setNewFile({...newFile,serviceAddress:event.target.value})}/></label>
          <DocumentTitleList value={documentsToServe} onChange={setDocumentsToServe} required/>
          <label>Service deadline <input type="date" required value={newFile.deadlineDate} onChange={event=>setNewFile({...newFile,deadlineDate:event.target.value})}/></label>
        </fieldset>}

        <div className="mcfw-actions">
          <button type="button" onClick={closeReview} disabled={pending}>Close without deciding</button>
          <button type="submit" disabled={pending}>{pending?(scanningObjectId?'Checking attachment…':'Confirming…'):'Confirm decision'}</button>
        </div>
      </form>}
    </section>}
  </section>;
}
